// Utils
import { brand } from '@/brand/brand';
import { getChainInfo, getTokenConversions } from '@/resources/wallet-service.resource';
import {
  findDefaultLayer1NativeCryptoAsset,
  isBitcoinCryptoAsset,
  isEvmCryptoAsset,
} from '@/providers/cryptoAssets';
// Icons
import BIN from '@/icons/logos/busd.jpg';
import BTC from '@/icons/logos/bitcoin.svg';
import ETH from '@/icons/logos/ethereum.svg';
import USDC from '@/icons/logos/usdc_icon.png';
import USDT from '@/icons/logos/usdt-token.svg';
import GALA from '@/icons/logos/gala-lite-node.svg';
import IZE from '@/icons/logos/galvan-dark.svg';

// See assets/icons/logos for brand icons available to Core - update as necessary, key should match coin symbol
const COIN_ICONS = {
  BIN: BIN.src,
  BTC: BTC.src,
  BUSD: BIN.src,
  ETH: ETH.src,
  GALA: GALA.src,
  IZE: IZE.src,
  USDC: USDC.src,
  USDT: USDT.src,
  WBTC: BTC.src,
  WETH: ETH.src,
};

export const extractCoinData = (coin = null) => {
  if (!coin) return;
  return {
    useEthGas: coin.coinData.useEthGas,
    priceUsd: coin.coinData.priceUsd,
    coinAmt: coin.coinData.balance,
    coinLogo: coin.coinData.icon,
    hasError: !!coin.coinData.hasError,
    usd: coin.coinData.usd || 0,
    walletId: coin.coinData.address,
    valid: coin.coinData.canSendFunds,
    cryptoAsset: coin?.coinData?.cryptoAsset || null,
  };
};

export async function handleGetChainInfo(fetcher, retryCount = 0) {
  try {
    return await fetcher(getChainInfo());
  } catch (error) {
    if (retryCount < 3) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return handleGetChainInfo(fetcher, retryCount + 1);
    } else {
      return null;
    }
  }
}

const getTokenPrice = (tokenPrices, symbol) =>
  tokenPrices[symbol?.toLowerCase()] || tokenPrices[symbol] || null;

const getWalletAddressForCryptoAsset = ({ addresses, cryptoAsset }) => {
  if (!addresses || !cryptoAsset) return null;
  if (isBitcoinCryptoAsset(cryptoAsset)) return addresses.btc || null;
  if (isEvmCryptoAsset(cryptoAsset)) return addresses.eth || null;
  return null;
};

const shouldLoadCryptoAsset = (cryptoAsset) => {
  if (!cryptoAsset.asset.settings.allowInApp) return false;
  if (cryptoAsset.traits.isDefaultLayer2Native) {
    return brand.pages.dashboard.useBrandCoin;
  }
  return true;
};

const usesDefaultLayer1NativeFee = (cryptoAsset, cryptoAssets) => {
  const feeCryptoAsset = cryptoAssets.find(
    (asset) => asset.uniqueKey === cryptoAsset.fee.cryptoAssetUniqueKey
  );

  return feeCryptoAsset?.traits.isDefaultLayer1 && feeCryptoAsset?.traits.isNative;
};

const createCoinData = ({
  address,
  balanceResult,
  cryptoAsset,
  icon,
  priceUsd,
  useEthGas,
}) => {
  const balance = balanceResult.balance;
  const hasUsableBalance =
    balance !== null && balance !== undefined && !balanceResult.hasError;
  const usd = hasUsableBalance ? (Number(balance) * (priceUsd || 0)).toString() : 0;

  return {
    coinData: {
      address,
      balance,
      balanceUsd: hasUsableBalance ? usd : null,
      canSendFunds: balanceResult.canSendFunds,
      hasError: balanceResult.hasError,
      icon,
      useEthGas,
      priceUsd,
      usd,
      cryptoAsset,
    },
  };
};

export async function handleGetWallet({
  addresses,
  dispatch,
  duplicateWallets,
  cryptoAssets,
  cryptoAssetProviderResolver,
  fetcher,
  isLegacy,
  setError,
  setLoading,
}) {
  setError('reset');

  if (setLoading) {
    dispatch({
      type: isLegacy ? 'SET_WALLET_LOADING' : 'SET_CORE_WALLET_LOADING',
      payload: true,
    });
  }

  const walletCryptoAssets = cryptoAssets.filter(shouldLoadCryptoAsset);
  const symbols = [
    ...new Set(walletCryptoAssets.map((asset) => asset.asset.canonicalName)),
  ];
  const tokenPrices = await fetcher(getTokenConversions({ symbols, currency: 'USD' }));
  const coins = [];
  try {
    const defaultLayer1NativeAsset = findDefaultLayer1NativeCryptoAsset(cryptoAssets);
    const brandCoinPrice =
      getTokenPrice(
        tokenPrices,
        cryptoAssets.find((asset) => asset.traits.isBrandErc20)?.asset.canonicalName
      ) || null;
    const balanceResults = await Promise.all(
      walletCryptoAssets.map(async (cryptoAsset) => {
        const address = getWalletAddressForCryptoAsset({ addresses, cryptoAsset });
        const balanceResult = address
          ? await cryptoAssetProviderResolver
              .requireProvider(cryptoAsset)
              .getBalance({ address, cryptoAsset })
          : { balance: null, canSendFunds: false, hasError: true };
        return { cryptoAsset, result: balanceResult };
      })
    );

    balanceResults.forEach(({ result: balanceResult, cryptoAsset }) =>
      coins.push(
        createCoinData({
          address: getWalletAddressForCryptoAsset({ addresses, cryptoAsset }),
          balanceResult,
          cryptoAsset,
          icon: COIN_ICONS[cryptoAsset.asset.canonicalName],
          priceUsd: cryptoAsset.traits.isDefaultLayer2Native
            ? brandCoinPrice
            : getTokenPrice(tokenPrices, cryptoAsset.asset.canonicalName),
          useEthGas:
            cryptoAsset.uniqueKey === defaultLayer1NativeAsset?.uniqueKey ||
            usesDefaultLayer1NativeFee(cryptoAsset, cryptoAssets),
        })
      )
    );

    if (coins.some((coin) => coin.coinData.hasError)) {
      setError({ coin: true });
    }

    const walletItemsArr = coins.map(extractCoinData).filter(Boolean);

    if (walletItemsArr.length) {
      dispatch({
        type: isLegacy ? 'SET_WALLET_STATE' : 'SET_CORE_WALLET_STATE',
        payload: walletItemsArr,
      });
    }
    // If duplicate wallets, retain "legacy" state information
    if (duplicateWallets) {
      dispatch({
        type: 'SET_HAS_DUPLICATE_WALLETS',
        payload: true,
      });
      dispatch({
        type: 'SET_WALLET_STATE',
        payload: walletItemsArr,
      });
    }

    return walletItemsArr;
  } finally {
    // Always reset loading states
    dispatch({
      type: isLegacy ? 'SET_WALLET_LOADING' : 'SET_CORE_WALLET_LOADING',
      payload: false,
    });
  }
}
